import { useNavigate } from "react-router-dom";
import { Common } from "@/components";
import { useImportOrder } from "../providers/ImportOrderContext";
import DownloadTemplate from "./DownloadTemplate";
import { readFileHeaders } from "../utils";
import { DocumentType, ImportMethod, TaxType } from "../models";
import { SUBSCRIPTION_PRODUCT_TYPE } from "@/configuration/globalVariable";
import { useEffect } from "react";
import { useAppSelector } from "@/sharedStore/hooks";

const UploadFile = () => {
  const { state, dispatch, handleCancel } = useImportOrder();
  const navigate = useNavigate();
  const subscriptionProductType = useAppSelector(
    (state) => state.authReducer.user?.subscribedProductType
  );

  useEffect(() => {
    if (subscriptionProductType === SUBSCRIPTION_PRODUCT_TYPE.TOMDELIVERY) {
      dispatch({
        type: "SET_IMPORT_OPTIONS",
        payload: {
          ...state,
          documentType: DocumentType.INVOICE,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = () => {
    if (state.selectedFile) {
      readFileHeaders({
        file: state.selectedFile,
        onFileRead: (headers: string[]) => {
          dispatch({
            type: "SET_FILE_COLUMNS",
            payload: headers,
          });
          navigate("#mapping-fields");
        },
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      dispatch({ type: "SET_FILE", payload: e.target.files[0] });
      dispatch({ type: "SET_MAPPING", payload: {} });
    }
  };

  return (
    <div className="relative">
      <div className="h-[calc(100vh-258px)]">
        <div className="flex flex-col lg:flex-row lg:space-x-20">
          <div className="flex-1 mb-6 lg:mb-0">
            {subscriptionProductType ===
              SUBSCRIPTION_PRODUCT_TYPE.TOMBUSINESS && (
              <div className="mb-6">
                <div className="mb-2 text-hd5">
                  Choose what you&apos;re importing
                </div>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <Common.Radio
                      wrapClassName="mb-0 mr-2"
                      checked={state.documentType === DocumentType.ORDER}
                      onChange={() =>
                        dispatch({
                          type: "SET_IMPORT_OPTIONS",
                          payload: {
                            ...state,
                            documentType: DocumentType.ORDER,
                            importMethod: ImportMethod.HEADERS_AND_DETAILS,
                          },
                        })
                      }
                      name="document_type"
                      id="document_order"
                      label="New sales orders - Not yet fulfilled. Ready to pick, pack and fulfill"
                    />
                  </div>
                  <div className="flex items-center">
                    <Common.Radio
                      wrapClassName="mb-0 mr-2"
                      checked={state.documentType === DocumentType.INVOICE}
                      onChange={() =>
                        dispatch({
                          type: "SET_IMPORT_OPTIONS",
                          payload: {
                            ...state,
                            documentType: DocumentType.INVOICE,
                          },
                        })
                      }
                      name="document_type"
                      id="document_invoice"
                      label="Sales invoices - Already fulfilled. Ready to be routed and delivered!"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="mb-6">
              <div className="mb-2 text-hd5">
                What level of detail are you importing?
              </div>
              <div className="space-y-2">
                <div className="flex items-center">
                  <Common.Radio
                    wrapClassName="mb-0 mr-2"
                    checked={state.importMethod === ImportMethod.HEADERS_ONLY}
                    disabled={state.documentType === DocumentType.ORDER}
                    onChange={() =>
                      dispatch({
                        type: "SET_IMPORT_OPTIONS",
                        payload: {
                          ...state,
                          importMethod: ImportMethod.HEADERS_ONLY,
                        },
                      })
                    }
                    name="import_method"
                    id="import_method_headers_only"
                    label="Summary only (no line items)"
                    subLabel="For simple delivery planning and Proof of Delivery on invoice numbers"
                  />
                </div>
                <div className="flex items-center">
                  <Common.Radio
                    wrapClassName="mb-0 mr-2"
                    checked={
                      state.importMethod === ImportMethod.HEADERS_AND_DETAILS
                    }
                    onChange={() =>
                      dispatch({
                        type: "SET_IMPORT_OPTIONS",
                        payload: {
                          ...state,
                          importMethod: ImportMethod.HEADERS_AND_DETAILS,
                        },
                      })
                    }
                    name="import_method"
                    id="import_method_headers_and_details"
                    label="Includes the line items (products, quantities, prices)"
                    subLabel="For more accurate Proof of Delivery and Credit Request on the go"
                  />
                </div>
              </div>
            </div>

            {state.importMethod === ImportMethod.HEADERS_AND_DETAILS && (
              <div className="mb-6">
                <div className="mb-2 font-medium">
                  Is the UnitPrice field tax inclusive or exclusive?
                </div>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <Common.Radio
                      wrapClassName="mb-0 mr-2"
                      checked={state.taxType === TaxType.EXCLUSIVE}
                      onChange={() =>
                        dispatch({
                          type: "SET_IMPORT_OPTIONS",
                          payload: {
                            ...state,
                            taxType: TaxType.EXCLUSIVE,
                          },
                        })
                      }
                      name="tax_type"
                      id="tax_exclusive"
                      label="Tax Exclusive"
                    />
                  </div>
                  <div className="flex items-center">
                    <Common.Radio
                      wrapClassName="mb-0 mr-2"
                      checked={state.taxType === TaxType.INCLUSIVE}
                      onChange={() =>
                        dispatch({
                          type: "SET_IMPORT_OPTIONS",
                          payload: {
                            ...state,
                            taxType: TaxType.INCLUSIVE,
                          },
                        })
                      }
                      name="tax_type"
                      id="tax_inclusive"
                      label="Tax Inclusive"
                    />
                  </div>
                </div>
              </div>
            )}

            <div>
              <div className="mb-2 font-medium text-hd5">
                Select File to Import
              </div>
              <div className="mb-1">
                <Common.Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    document.getElementById("file_upload")?.click()
                  }
                >
                  Browse
                </Common.Button>
                <input
                  type="file"
                  id="file_upload"
                  className="hidden"
                  accept=".csv,.xls,.xlsx,.xlsm"
                  onChange={handleFileChange}
                />
                <span className="ml-2 text-neutral-50 text-sm font-normal">
                  {state.selectedFile?.name || "No file selected"}
                </span>
              </div>
              <div className="text-sm font-normal text-neutral-50">
                Import file format are CSV and Excel file (xls, xlsx and xlsm)
              </div>
              <div className="text-sm font-normal text-red mt-1">
                Dates must be in the format dd/mm/yyyy (e.g. 25/12/2024)
              </div>
            </div>
          </div>

          <div className="w-full lg:w-1/2 bg-white p-6 rounded border border-neutral-20">
            <DownloadTemplate />
          </div>
        </div>
      </div>
      <div className="flex justify-end space-x-3 mt-5">
        <Common.Button
          color="transparent"
          onClick={() =>
            handleCancel({ mode: "cancel", needConfirm: !!state.selectedFile })
          }
        >
          Cancel
        </Common.Button>
        <Common.Button onClick={handleSubmit} disabled={!state.selectedFile}>
          Map Data Fields
        </Common.Button>
      </div>
    </div>
  );
};

export default UploadFile;
